import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "react-query";
import { useParams } from "react-router-dom";
import { axios } from "../lib/request";
import { answerQueryKey, infiniteAnswerCommentsUri, infiniteAnswerCommentsQueryKey, answerCommentsQueryKey, answerAdjustmentsQueryKey } from "../lib/refresh";
import { getErrorMessage } from "../lib/error";
import { useSetRecoilState } from "recoil";
import { alertState } from "../common/AlertDisplay/AlertDisplay.state";

export const fetchAnswer = async (id, game_id) => {
    const url = `${process.env.REACT_APP_API_ANSWER_RETRIEVE_URL}/${id}.json?game_id=${game_id}`;
    const { data } = await axios.get(url);
    return data;
};

export const useAnswerDataQuery = (id) => {
    const { game_id } = useParams();
    const setAlert = useSetRecoilState(alertState);
    return useQuery(answerQueryKey(id), () => fetchAnswer(id, game_id), {
        suspense: true,
        useErrorBoundary: false,
        onError: (error) => {
            const message = getErrorMessage(error);
            setAlert({ type: "error", message, closable: true });
        }
    });
};

export const useAnswerQuery = (id) => {
    const { data } = useAnswerDataQuery(id);
    return data?.answer;
};

export const fetchCommentStream = async (answer_id, token) => {
    try {
        const params = { token, per_page: 5 };
        const response = await axios.get(infiniteAnswerCommentsUri(answer_id), { params });
        const { data = {} } = response || {};

        // Filter out empty comments
        data.comments = (data.comments || []).filter(c => c?.comment !== null)
        return data;
    } catch (e) {
        console.error(e)
    }
};

const fetchAllCommentsStreams = ({ pageParam }, answer_id) => {
    return fetchCommentStream(answer_id, pageParam);
}

const deleteComment = (comment_id, game_id, answer_id) => axios.delete(
    `${process.env.REACT_APP_API_ANSWERS_COMMENTS_UPDATE_URL}/${comment_id}.json?game_id=${game_id}&answer_id=${answer_id}`
)

export const fetchComments = async ({ answerId, page }) => {
    try {
        const params = { per_page: 3, page };
        const response = await axios.get(infiniteAnswerCommentsUri(answerId), { params });
        const { data = {} } = response || {};

        // Filter out empty comments
        data.comments = (data.comments || []).filter(c => c?.comment !== null)
        return data;
    } catch (e) {
        console.error(e)
    }
};

export const useAnswersCommentsQuery = ({ answerId, page }) => {
    return useQuery(
        answerCommentsQueryKey({ answerId, page }),
        () => fetchComments({ answerId, page }),
    );
}

export const useInfiniteAnswersComments = (answer_id) => {
    return useInfiniteQuery(infiniteAnswerCommentsQueryKey(answer_id), config => fetchAllCommentsStreams(config, answer_id), {
        getNextPageParam: (lastPage, pages) => {
            const ids = (lastPage?.comments || []).map(({ id }) => parseInt(id));
            const { count = 0 } = lastPage || {};
            const loaded = pages.reduce((sum, page = []) => (sum += page?.length), 0);

            if (lastPage?.comments?.length === 0 || count <= loaded) {
                return undefined;
            }

            return Math.min(...ids);
        },
        suspense: false,
    });
}

export const useDeleteAnswersCommentMutation = (answer_id) => {
    const { game_id } = useParams()
    const { data: oldPagesArray } = useInfiniteAnswersComments(answer_id)
    const queryClient = useQueryClient()
    const { mutateAsync, isLoading } = useMutation(
        (comment) => deleteComment(comment.id, game_id, answer_id),
        {
            onMutate: (data) => {
                // Filter out deleted comment
                const newPagesArray =
                    oldPagesArray?.pages.map((page) => {
                        if (page?.comments?.length > 0) {
                            page.comments = page.comments.filter((val) => val.id !== data.id)
                        }
                        return page
                    }) ?? []

                // Implement optimistic update for comment deletion
                queryClient.setQueryData(infiniteAnswerCommentsQueryKey(answer_id), (data) => ({
                    pages: newPagesArray,
                    pageParams: data.pageParams,
                }))
            },
        }
    )
    return {
        deleteComment: mutateAsync,
        isDeletingComment: isLoading,
    }
}

export const fetchAdjustments = async ({ answerId, gameId }) => {
    try {
        const params = { game_id: gameId, answer_id: answerId };
        const response = await axios.get(process.env.REACT_APP_API_ADJUSTMENTS_URL, { params });
        const { data = {} } = response || {};
        return data;
    } catch (e) {
        console.error(e)
    }
};

const deleteAdjustment = (id, game_id) => axios.delete(
    `${process.env.REACT_APP_API_ADJUSTMENTS_UPDATE_URL}/${id}.json?game_id=${game_id}`
)

const updateAdjustment = (data) => axios.post(
    `${process.env.REACT_APP_API_ADJUSTMENTS_URL}`,
    data,
)

export const useAnswersAdjustmentsQuery = ({ answerId }) => {
    const { game_id } = useParams();

    return useQuery(
        answerAdjustmentsQueryKey({ answerId }),
        () => fetchAdjustments({ answerId, gameId: game_id }),
    );
}

export const useDeleteAnswersAdjustmentMutation = ({ answerId }) => {
    const { game_id } = useParams()
    const queryClient = useQueryClient()
    const previousAdjustments = queryClient.getQueryData(answerAdjustmentsQueryKey({ answerId }))
    return useMutation(
        (adjustmentId) => deleteAdjustment(adjustmentId, game_id),
        {
            onSuccess: (_, adjustmentId) => {
                const newAdjustments = { ...previousAdjustments }
                newAdjustments.adjustments = newAdjustments.adjustments.filter(adj => {
                    return adj.id !== adjustmentId
                })

                queryClient.setQueryData(answerAdjustmentsQueryKey({ answerId }), newAdjustments);
            },
        }
    )
}

export const useUpdateAnswersAdjustmentMutation = ({ answerId }) => {
    const { game_id } = useParams()
    const queryClient = useQueryClient()

    return useMutation(
        ({ id, ...data }) => updateAdjustment({
            ...data,
            game_id,
            answer_id: answerId,
        }),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(answerAdjustmentsQueryKey({ answerId }));
            },
        }
    )
}

export const useRegenerateMutation = () => {
    return useMutation(
        (id) => axios.put(
            `${process.env.REACT_APP_API_REGENERATE_URL}`,
            { id },
        ),
    )
}